import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import { API_BASE } from '../lib/apiBase';

// Rewrite `/uploads/foo.jpg` → `${API_BASE}/api/uploads/foo.jpg` so the
// request hits the backend StaticFiles mount on production (the live CDN
// only proxies `/api/*` to the backend; bare `/uploads/*` would 404).
const ABS = (url) => (
  url && url.startsWith('/uploads/')
    ? `${API_BASE}/api${url}`
    : url
);

// "Explore by Location" — a sibling of CategoriesSection, but tiled by
// destination. Order is fixed: Sitges, Barcelona, Tarragona, Day Trips.
// Clicking a tile navigates to /Explore with the matching ?location= param,
// which is read by ExperiencesPage and applied to the locations filter.
//
// Unsplash URLs use format-auto (`fm=webp`) + quality=80 which cuts bytes
// roughly in half vs the default JPEG response, and we serve a proper
// srcset with 1x/2x variants so retina screens stay sharp without wasting
// bandwidth on regular DPR=1 displays. Admin-uploaded overrides (AVIF +
// WebP + JPEG sets) still take priority when present.
//
// Sizing: a tile is at most ~296px CSS wide (desktop 4-col) => 592px @DPR2.
// So 1x=400 covers non-retina and 2x=800 covers retina with margin; the
// previous 600/1200 pair was ~2x oversized and cost ~1.5MB on mobile.
const UNSPLASH = (id) => ({
  webp1x: `https://images.unsplash.com/${id}?fm=webp&q=80&w=400&h=300&fit=crop`,
  webp2x: `https://images.unsplash.com/${id}?fm=webp&q=80&w=800&h=600&fit=crop`,
  jpg: `https://images.unsplash.com/${id}?fm=jpg&q=80&w=400&h=300&fit=crop`,
});

const LOCATIONS = [
  { key: 'sitges',     labelKey: 'destination_sitges',     photo: UNSPLASH('photo-1583422409516-2895a77efded') },
  { key: 'barcelona',  labelKey: 'destination_barcelona',  photo: UNSPLASH('photo-1539037116277-4db20889f2d4') },
  { key: 'tarragona',  labelKey: 'destination_tarragona',  photo: UNSPLASH('photo-1601581875309-fafbf2d3ed3a') },
  { key: 'day_trips',  labelKey: 'destination_day_trips',  photo: UNSPLASH('photo-1500530855697-b586d89ba3ee') },
];

export function LocationsSection() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Map of slug → { webp_1x, webp_2x, jpeg_1x } overrides set by admin.
  // Falls back to the hardcoded Unsplash URL above when missing.
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/locations`).then((res) => {
      if (cancelled) return;
      const map = {};
      (res.data?.locations || []).forEach((loc) => {
        if (loc?.images) map[loc.slug] = loc.images;
      });
      setOverrides(map);
    }).catch(() => { /* fall back to defaults silently */ });
    return () => { cancelled = true; };
  }, []);

  const handleClick = (loc) => {
    navigate(`/Explore?location=${encodeURIComponent(loc.key)}`);
  };

  return (
    <section className="py-[30px] sm:py-[40px]" data-testid="locations-section" id="locations">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
        <h2 className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight">
          {t('locations_title')}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6">
          {LOCATIONS.map((loc, i) => {
            const name = t(loc.labelKey);
            const ov = overrides[loc.key];
            const hasOv = !!(ov && (ov.avif_1x || ov.webp_1x || ov.jpeg_1x));
            // Admin overrides ship a 600px _1x and a 1200px _2x. The tile is at
            // most ~592px @DPR2, so the 600px _1x already covers every device —
            // we serve it as the ONLY source and never fetch the oversized _2x.
            // Unsplash defaults keep a real 1x(400)/2x(800) pair.
            const avif1x = hasOv ? ABS(ov.avif_1x) : null;
            const avif2x = null;
            const webp1x = hasOv ? ABS(ov.webp_1x) : loc.photo.webp1x;
            const webp2x = hasOv ? null : loc.photo.webp2x;
            const jpeg1x = hasOv ? (ABS(ov.jpeg_1x) || ABS(ov.webp_1x)) : loc.photo.jpg;
            const fallbackSrc = jpeg1x;
            return (
              <motion.button
                key={loc.key}
                data-testid={`location-${loc.key}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                onClick={() => handleClick(loc)}
                className="group text-left bg-transparent transition-all cursor-pointer"
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                  <picture>
                    {avif1x && (
                      <source
                        type="image/avif"
                        srcSet={avif2x ? `${avif1x} 1x, ${avif2x} 2x` : avif1x}
                      />
                    )}
                    {webp1x && (
                      <source
                        type="image/webp"
                        srcSet={webp2x ? `${webp1x} 1x, ${webp2x} 2x` : webp1x}
                      />
                    )}
                    <img
                      src={fallbackSrc}
                      alt={name}
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </picture>
                </div>
                <p
                  className="mt-3 text-left text-[17px] sm:text-[19px] font-bold leading-tight"
                  style={{ color: '#0F3B63' }}
                >
                  {name}
                </p>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
